import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {Add as AddIcon} from "@mui/icons-material";
import {sugerirChave, validarChave} from "../catalogoRegras";
import {createCatalogEntity} from "../SistemasService";
import {getBackendEntityMessage} from "../sistemasUtils";
import {listarTudo} from "../useSistemaPagina";
import {validarSigla} from "./DadosSecao";

const formVazio = () => ({nome: '', sigla: '', chave: '', chaveManual: false});

// "Novo sistema" pede só nome, sigla e chave. O sistema nasce em desenvolvimento, sem módulos, e o resto
// se preenche na página dele (BBF-174).
export default function NovoSistemaDialog({aberto, onFechar, onCriado}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [form, setForm] = useState(formVazio);
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (aberto) {
            setForm(formVazio());
            setErro('');
        }
    }, [aberto]);

    const mudar = (campo) => (event) => {
        const valor = campo === 'sigla' ? event.target.value.toUpperCase() : event.target.value;
        setForm((atual) => {
            const novo = {...atual, [campo]: valor};
            if (campo === 'chave') novo.chaveManual = true;
            if (campo === 'nome' && !atual.chaveManual) novo.chave = sugerirChave(valor);
            return novo;
        });
    };

    const criar = async (event) => {
        event.preventDefault();
        const nome = form.nome.trim();
        const sigla = form.sigla.trim();
        const chave = form.chave.trim();
        if (!nome) return setErro('Dê um nome ao sistema.');
        const erroDaSigla = validarSigla(sigla);
        if (erroDaSigla) return setErro(`${erroDaSigla} Ela aparece no ícone do sistema.`);
        const erroDaChave = validarChave(chave);
        if (erroDaChave) return setErro(erroDaChave);

        setSalvando(true);
        try {
            await createCatalogEntity('sistemas', {nome, sigla, chave});
            // O POST não devolve o sistema: o id sai da busca pela chave, que é única.
            const criado = (await listarTudo('sistemas', {chave})).find((item) => item.chave === chave);
            onCriado(criado);
        } catch (error) {
            setErro(getBackendEntityMessage(error, 'Não foi possível criar o sistema.'));
        } finally {
            setSalvando(false);
        }
        return undefined;
    };

    return (
        <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="sm" fullScreen={fullScreen}>
            <Box component="form" onSubmit={criar} noValidate>
                <DialogTitle>Novo sistema</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                            <TextField label="Nome" value={form.nome} onChange={mudar('nome')} placeholder="Agenda de Campo" required fullWidth autoFocus inputProps={{autoComplete: 'off'}} />
                            <TextField
                                label="Sigla"
                                value={form.sigla}
                                onChange={mudar('sigla')}
                                placeholder="AC"
                                required
                                sx={{width: {sm: 140}}}
                                inputProps={{maxLength: 3, autoComplete: 'off'}}
                            />
                        </Stack>
                        <TextField
                            label="Chave"
                            value={form.chave}
                            onChange={mudar('chave')}
                            placeholder="agenda-de-campo"
                            required
                            fullWidth
                            InputProps={{sx: {fontFamily: 'monospace'}}}
                            inputProps={{spellCheck: false, autoComplete: 'off'}}
                            helperText={'Sugerida pelo nome. Letras minúsculas sem acento, números e os separadores ".", "_" ou "-". Não muda depois de criada: permissões, integrações e dados de uso apontam para ela.'}
                        />
                        <Alert severity="info" variant="outlined">
                            O sistema nasce <strong>em desenvolvimento</strong>, sem módulos. O resto se preenche na página dele, salvo na hora e mesmo incompleto. As pendências mostram o que falta para pôr à venda.
                        </Alert>
                        {erro ? <Alert severity="error">{erro}</Alert> : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onFechar}>Cancelar</Button>
                    <Button type="submit" variant="contained" startIcon={<AddIcon />} disabled={salvando}>Criar e abrir</Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
}
